<template>
  <div v-if="loaded">
    <CRow v-if="advanced">
      <CCol>
        <label v-translate translate-context="form_configurator.text.label">Field Key</label>
        <CInput v-model="key" @input="save" :placeholder="keyPlaceholder"/>
      </CCol>
    </CRow>
    <CRow>
      <CCol>
        <label v-translate translate-context="form_configurator.select.label">Field name</label>
        <PTranslatableTextInput v-model="name" @input="save" :langs="langs"/>
      </CCol>
    </CRow>
    <CRow v-if="advanced">
      <CCol>
        <label v-translate translate-context="form_configurator.select.label">Field Size</label>
        <CSelect v-model="size" @change="save" :options="fieldSizeOptions" :placeholder="$pgettext('form_configurator.select', 'Select field size')"/>
      </CCol>
    </CRow>
    <PFRow v-if="advanced">
      <template #title>
        <label class="font-weight-medium">
          <span v-translate translate-context="form_configurator.select.label">Order options alphabetically</span>
          <CIcon name="cipInfo" size="sm" class="ml-2"
                 v-c-tooltip="{
                   content: $pgettext('form_configurator.select.help', 'When enabled, options are shown sorted by the user\'s language at form display time. Manual sorting is disabled.'),
                   placement: 'top',
                   appendToBody: true
                 }"/>
        </label>
      </template>
      <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
               :checked.sync="params.sort_alphabetically"
               @update:checked="save"/>
    </PFRow>
    <CRow>
      <CCol>
        <draggable v-if="!params.sort_alphabetically" v-model="params.options" @end="updateOptionSorting" handle=".handle" ghost-class="ghost">
          <transition-group type="transition" :name="'flip-list'">
            <div v-for="option in params.options" :key="option.value"
                 class="form-generator__field form-generator__field--modal">
              <div class="flex-grow-1 mr-4">
                <CRow>
                  <CCol sm="6">
                    <div class="p-3 font-weight-bold">{{ option.value }}</div>
                  </CCol>
                  <CCol sm="6">
                    <div class="p-3">{{ option.label['it'] }}</div>
                  </CCol>
                </CRow>
              </div>
              <div class="handle p-3">
                <CIcon size="lg" name="cip-hamburger"/>
              </div>
              <div class="mr-3">
                <CButtonClose  class="list-item-closer"  @click.prevent="deleteOption(option.value)">
                  <CIcon name="cipClose" size="custom-size" width="15" />
                </CButtonClose>
              </div>

            </div>
          </transition-group>
        </draggable>
        <div v-else>
          <div v-for="option in displayedOptions" :key="option.value"
               class="form-generator__field form-generator__field--modal">
            <div class="flex-grow-1 mr-4">
              <CRow>
                <CCol sm="6">
                  <div class="p-3 font-weight-bold">{{ option.value }}</div>
                </CCol>
                <CCol sm="6">
                  <div class="p-3">{{ option.label['it'] }}</div>
                </CCol>
              </CRow>
            </div>
            <div class="mr-3">
              <CButtonClose class="list-item-closer" @click.prevent="deleteOption(option.value)">
                <CIcon name="cipClose" size="custom-size" width="15"/>
              </CButtonClose>
            </div>
          </div>
        </div>
      </CCol>
    </CRow>
    <CRow class="mb-4">
      <CCol md="6" v-if="advanced">
        <CInput v-model="newOption.value" :label="$pgettext('form_configurator.select','Value')"
                :placeholder="newOption.valuePlaceholder"/>
      </CCol>
      <CCol md="6">
        <PTranslatableTextInput
            v-model="newOption.label"
            :placeholder="$pgettext('form_configurator.select','Label')"
            :label="$pgettext('form_configurator.select','Label')"
            :langs="langs"
            @input="updatePlaceholder"
        />
      </CCol>
    </CRow>
    <CRow>
      <CCol class="d-flex align-items-center">
        <CButton color="success" variant="outline" @click="addOption">
          <span v-translate translate-context="form_configurator.select">Add option</span>
        </CButton>
        <template v-if="advanced">
          <CButton class="ml-2" color="primary" variant="outline" @click="exportCsv"
                   :disabled="!params.options || params.options.length === 0">
            <span v-translate translate-context="form_configurator.select">Export CSV</span>
          </CButton>
          <CButton class="ml-2" color="primary" variant="outline" @click="triggerImport">
            <span v-translate translate-context="form_configurator.select">Import CSV</span>
          </CButton>
          <input ref="csvInput" type="file" accept=".csv,text/csv" class="d-none"
                 @change="onCsvFileSelected"/>
        </template>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import PTranslatableTextInput from "@/domain/core/components/PTranslatableTextInput.vue";
import PFRow from "@/domain/core/components/PFRow.vue";
import {generateKey, fieldSizeOptions, defaultFieldSize} from "@/domain/formConfigurator/fields";
import draggable from 'vuedraggable'
import Papa from 'papaparse'
import downloader from "@/domain/core/utils/downloader"

export default {
  name: "FormSelectInput",
  components: {
    draggable,
    PTranslatableTextInput,
    PFRow,
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    advanced: {
      type: Boolean,
      required: true
    },
    langs: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      loaded: false,
      name: '',
      key: '',
      keyPlaceholder: '',
      size: defaultFieldSize,
      params: {
        options: [],
      },
      newOption: {
        value: '',
        valuePlaceholder: '',
        label: {}
      }
    }
  },
  computed: {
    fieldSizeOptions() {
      return fieldSizeOptions
    },
    displayedOptions() {
      if (!this.params.sort_alphabetically) return this.params.options
      const labelOf = opt => (opt.label && (opt.label.it || Object.values(opt.label)[0])) || ''
      return [...this.params.options].sort((a, b) => labelOf(a).localeCompare(labelOf(b), 'it'))
    }
  },
  emits: ['update:data'],
  watch: {
    data: function () {
      this.init()
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.key = this.data.key ?? ''
      this.name = this.data.name ?? {}
      this.size = this.data.size ?? defaultFieldSize
      const incoming = this.data.params ?? {}
      this.params = {
        options: incoming.options ?? [],
        sort_alphabetically: incoming.sort_alphabetically ?? false
      }
      this.newOption.label = {}
      this.newOption.valuePlaceholder = ''
      this.newOption.value = ''
      this.loaded = true
    },
    save() {
      this.keyPlaceholder = generateKey(this.name)
      this.$emit('update:data', {
        ...this.data,
        key: this.key,
        name: this.name,
        size: this.size,
        params: this.params
      })
    },
    addOption() {
      if (this.newOption.label === '') return
      if (this.newOption.value === '') this.newOption.value = generateKey(this.newOption.label)

      this.params.options.push({
        value: this.newOption.value,
        label: this.newOption.label
      })
      this.newOption.value = ''
      this.newOption.valuePlaceholder = ''
      this.newOption.label = {}
      this.save()
    },
    deleteOption(value) {
      this.params.options = this.params.options.filter(option => option.value !== value)
      this.save()
    },
    updatePlaceholder() {
      this.newOption.valuePlaceholder = generateKey(this.newOption.label)
    }, updateOptionSorting() {
      this.params.options = this.params.options.map((input, index) => {
        input.order = index + 1
        return input
      })
      this.save()
    },
    exportCsv() {
      // Always export the full set of supported languages so the CSV doubles as a translation template,
      // even when the form is in non-advanced mode (which only shows Italian inline).
      const allLangs = process.env.VUE_APP_SUPPORTED_LANGS.split(',')
      const header = ['value', ...allLangs.map(l => `label_${l}`)]
      const rows = (this.params.options || []).map(option => {
        return [option.value, ...allLangs.map(l => (option.label && option.label[l]) || '')]
      })
      const csv = Papa.unparse([header, ...rows])
      const fieldKey = this.key || generateKey(this.name) || 'options'
      downloader.downloadBlob(csv, `${fieldKey}_options.csv`, 'text/csv;charset=utf-8', {bom: true})
    },
    triggerImport() {
      this.$refs.csvInput.value = ''
      this.$refs.csvInput.click()
    },
    onCsvFileSelected(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: h => h.trim(),
        complete: result => this.applyParsedCsv(result),
        error: err => this.notifyError(`${this.$pgettext('form_configurator.select', 'Failed to parse CSV')}: ${err.message}`)
      })
    },
    applyParsedCsv(result) {
      const fields = result.meta && result.meta.fields ? result.meta.fields : []
      if (!fields.includes('value')) {
        this.notifyError(this.$pgettext('form_configurator.select', 'CSV must contain a "value" column'))
        return
      }
      const labelCols = fields.filter(f => f.startsWith('label_'))
      if (labelCols.length === 0) {
        this.notifyError(this.$pgettext('form_configurator.select', 'CSV must contain at least one "label_<lang>" column'))
        return
      }

      const seen = new Set()
      let skippedEmpty = 0
      let skippedDuplicate = 0
      const newOptions = []
      for (const row of result.data) {
        const value = (row.value || '').trim()
        if (!value) {
          skippedEmpty++
          continue
        }
        if (seen.has(value)) {
          skippedDuplicate++
          continue
        }
        seen.add(value)
        const label = {}
        for (const col of labelCols) {
          const lang = col.substring('label_'.length)
          const text = (row[col] || '').trim()
          if (text) label[lang] = text
        }
        newOptions.push({value, label, order: newOptions.length + 1})
      }

      if (newOptions.length === 0) {
        this.notifyError(this.$pgettext('form_configurator.select', 'No valid rows found in CSV'))
        return
      }

      const apply = () => {
        this.params.options = newOptions
        this.save()
        const messages = [
          this.$gettextInterpolate(
            this.$pgettext('form_configurator.select', '%{count} options imported'),
            {count: newOptions.length}
          )
        ]
        if (skippedEmpty > 0) messages.push(this.$gettextInterpolate(
          this.$pgettext('form_configurator.select', '%{count} rows skipped (empty value)'),
          {count: skippedEmpty}
        ))
        if (skippedDuplicate > 0) messages.push(this.$gettextInterpolate(
          this.$pgettext('form_configurator.select', '%{count} rows skipped (duplicate value)'),
          {count: skippedDuplicate}
        ))
        this.notifySuccess(messages.join(' — '))
      }

      const hasExisting = this.params.options && this.params.options.length > 0
      if (hasExisting) {
        this.confirm({
          title: this.$pgettext('form_configurator.select', 'Replace all options?'),
          message: this.$gettextInterpolate(
            this.$pgettext('form_configurator.select', 'This will replace the %{existing} existing options with %{incoming} from the CSV. This action cannot be undone.'),
            {existing: this.params.options.length, incoming: newOptions.length}
          ),
          cb_confirm: apply
        })
      } else {
        apply()
      }
    }
  }
}
</script>